import { PolicyPage } from './PolicyPage.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';

/**
 * The security one-pager (`docs/landing/security-onepager.md`), published for the first time.
 *
 * Counsel-cleared 2026-08-08 and marked "Yes — publish as-is" in `docs/landing/README.md`'s table;
 * nothing in this app ever rendered it ([B-271](../../../../docs/platform/TODO.md)). Its audience is
 * a buyer's IT/Security reviewer, which is why the honest-limits section is kept in full rather than
 * summarised — that section is the reason the document is credible.
 *
 * ⚠️ **Transcribed, not summarised.** The wording of a security claim is the claim; paraphrasing
 * "we do not hold SOC 2" into something softer is how a page starts implying a certificate.
 */
export interface SecurityOnePagerProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

const QUESTIONS: { q: string; a: React.ReactNode }[] = [
  {
    q: 'Are you SOC 2 certified?',
    a: (
      <>
        <strong>No.</strong> Type I targeted within 12 months of launch; controls and evidence
        automation are built in from day one. We will not claim a certificate we do not hold.
      </>
    ),
  },
  {
    q: 'Where is our data?',
    a: (
      <>
        <strong>The region you choose (EU or US)</strong>, and it never leaves it — storage,
        database, processing, AI calls, email, audit, all pinned together. Your{' '}
        <strong>account metadata</strong> (organisation name, region, plan, an opaque user
        reference, billing ids) sits in <strong>one UK database</strong> and holds no records and no
        direct identifiers. The two are separate by design and both are stated in the DPA.
      </>
    ),
  },
  {
    q: 'How do you authenticate?',
    a: (
      <>
        Passwordless magic link via a managed identity provider; sessions are <code>HttpOnly</code>,{' '}
        <code>Secure</code>, first-party cookies. <strong>A magic link is one factor (email
        possession) and we never call it 2FA.</strong> Enterprise SSO (OIDC) is the path by which MFA
        arrives, via your own IdP where your policy already lives — it is a Phase-5 item, not
        available today.
      </>
    ),
  },
  {
    q: 'How is authorisation enforced?',
    a: (
      <>
        Four roles — Owner / Admin / Reviewer / Viewer — resolved <strong>server-side on every
        request</strong> from a permission matrix held as data in one place, <strong>fail-closed</strong>:
        a mutating route the matrix does not declare is refused rather than open. The role is read
        per request, so a demotion or removal takes effect on the <strong>next</strong> request with
        an unchanged token.
      </>
    ),
  },
  {
    q: 'How are our OAuth tokens held?',
    a: (
      <>
        Never sent to a browser, never logged. Envelope encryption — a per-organisation data key,
        wrapped by a key-encryption key in a managed secrets store. Plaintext exists only in memory
        for the duration of a request. Connections start <strong>read-only</strong>; write scopes are
        requested later, <strong>per action type</strong>, at first approval.
      </>
    ),
  },
  {
    q: 'How are tenants isolated?',
    a: (
      <>
        Per-customer storage prefixes, a database per customer, per-customer orchestration state, and
        single-tenant processing containers that handle one organisation&rsquo;s data per job and
        wipe scratch afterwards. Cross-organisation access fails closed and raises a security event.
        Our own operator console reads <strong>counts and account metadata only</strong>, enforced by
        a test that pins exactly which fields may cross the boundary.
      </>
    ),
  },
  {
    q: 'What is encrypted?',
    a: (
      <>
        TLS 1.2+ in transit (1.3 preferred), HSTS, no plaintext listeners. At rest: provider
        encryption, <strong>plus client-side encryption under your organisation&rsquo;s own key for
        snapshots and raw pulls</strong> — the highest-density personal data we hold — so storage
        access alone yields ciphertext.
      </>
    ),
  },
  {
    q: 'Can we get an audit trail?',
    a: (
      <>
        Yes. Append-only, <strong>hash-chained</strong>, with daily chain-head anchors written to
        separate storage. Readable and exportable by you with an integrity manifest, and you can
        verify the chain yourself. 2-year retention. The external countersigning authority is{' '}
        <strong>not yet contracted</strong>, so tamper-evidence currently rests on our own digest —
        stated on every export rather than glossed.
      </>
    ),
  },
  {
    q: 'What does AI see?',
    a: (
      <>
        The <strong>match-relevant fields of one pair</strong> — not full records, not related
        records, not notes unless you configure a field as evidence. Advisory only; a person approves
        every change. <strong>Bring your own keys</strong> and there is no Dedupr-side AI
        sub-processing at all. Every AI-influenced decision has a receipt holding field{' '}
        <strong>names and hashes, never values</strong>.
      </>
    ),
  },
  {
    q: 'What happens in a breach?',
    a: (
      <>
        Notification without undue delay and <strong>within 72 hours</strong> of our determining a
        personal-data breach occurred; status page within 30 minutes of a SEV-1, direct contact
        within 1 hour if you are affected, written post-mortem within 5 business days. A{' '}
        <strong>cross-customer leak is SEV-1 regardless of size</strong>.
      </>
    ),
  },
];

const NOT_OFFERED = [
  <><strong>SOC 2 or ISO 27001 certification</strong> — neither is held.</>,
  <><strong>HIPAA / a BAA</strong> — out of scope; PHI workloads declined.</>,
  <><strong>SSO/SAML or SCIM</strong> — Phase 5, not at launch.</>,
  <><strong>Penetration test results</strong> — none performed yet; scheduled before general availability.</>,
  <><strong>A status page or live uptime figures</strong> — not yet running.</>,
  <><strong>Customer references or logos</strong> — Dedupr is pre-launch and has none. We will not invent them.</>,
  <><strong>Real-time sync</strong> — cursor-based incremental sync plus a reconciliation sweep; the fastest documented cadence is hourly.</>,
  <><strong>A merge inside your CRM</strong> — Dedupr writes the surviving values and <strong>leaves the duplicate in place</strong>, because a native CRM merge is irreversible in a way our 30-day rollback promise cannot cover.</>,
  <><strong>Automatic remediation of your automations</strong> — the automation, sequence and integration audits are read-only proposals; we execute nothing there.</>,
  <><strong>Guaranteed clean data</strong> — what we build is a <strong>gate plus attestation</strong>: rows failing your contract are quarantined and every load is attested. That is defensible; an unqualified guarantee is not.</>,
];

export function SecurityOnePager(props: SecurityOnePagerProps) {
  return (
    <PolicyPage
      section="Trust"
      title="Security one-pager"
      intro="What a buyer's security reviewer needs, including the things we do not offer — stated rather than omitted."
      reviewed="2026-08-08"
      counselCleared
      {...props}
    >
      <h2>What Dedupr is, for a security reviewer</h2>
      <p>
        A B2B SaaS <strong>data processor</strong>. Customers connect their own CRM (Salesforce,
        HubSpot) and document stores (SharePoint/OneDrive, Google Drive). We profile the data, detect
        duplicates and quality problems, and — <strong>only after a named human approves each
        change</strong> — write corrections back or export cleaned copies to storage the customer
        owns. Every mutating action is snapshotted and reversible for 30 days. Data is pinned to a
        customer-chosen region.
      </p>
      <p>
        <strong>We are not a controller of the customer&rsquo;s data.</strong> We do not enrich it,
        we do not train on it, we do not sell or share it.
      </p>

      <h2>The ten answers most vendor questionnaires need</h2>
      <dl className="land-legal__qa">
        {QUESTIONS.map((row) => (
          <div key={row.q}>
            <dt>{row.q}</dt>
            <dd>{row.a}</dd>
          </div>
        ))}
      </dl>

      <h2>The property worth understanding before the rest</h2>
      <p>
        <strong>Containment for a data-integrity incident is a rollback, not a restore.</strong>
      </p>
      <p>
        Every write Dedupr makes to your systems is preceded by a snapshot, and every write is
        reversible for 30 days — a product feature that doubles as an incident control. A rogue,
        buggy or malicious mass-write inside your CRM is not a catastrophe requiring your own
        backups; it is an action with a pre-image that can be reversed and verified.
      </p>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>The boundary:</strong> we snapshot <strong>Dedupr-initiated changes only</strong>.
          Your source systems remain your systems of record and their backup is not our
          responsibility — that is in the DPA in those words.
        </div>
      </div>

      <h2>Availability</h2>
      <p>
        <strong>99.9% monthly, at every paid tier, no service credits.</strong> Measured by external
        checks from three regions at one-minute intervals; two-of-three failure counts a minute as
        down; 5xx from our edge counts and 4xx does not; calendar month, UTC. Exclusions are named
        and capped — scheduled maintenance announced 72 h ahead and capped at 4 h/month, your own
        systems, data planes we do not operate, your network, suspension for non-payment.{' '}
        <strong>A Cloudflare platform incident is not excluded.</strong>
      </p>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>The measurement is not running yet</strong> — the definition is published so the
          commitment can be audited against something precise; the monitoring is specified and not
          configured, and there is no status page. <strong>No service credits</strong>, said plainly
          rather than omitted: a credit is a priced liability we have not modelled. We would rather
          commit to a number and be measured against it than attach a payout we cannot stand behind.
        </div>
      </div>

      <h2>Retention (the rows most often asked about)</h2>
      <ul>
        <li>Raw pull files <strong>7 days</strong> · staged data purged <strong>30 days</strong> after you disconnect a source</li>
        <li><strong>Snapshots 30 days</strong> (35 physical) — it <em>is</em> the rollback window and cannot be set shorter</li>
        <li>Exports we hold <strong>90 days</strong> · <strong>audit log 2 years</strong> · AI receipts 2 years · evidence packs 90 days</li>
        <li><strong>Account closure:</strong> full purge of your records within 30 days, export offered first, and a signed purge attestation retained afterwards holding no field values</li>
      </ul>

      <h2>GDPR</h2>
      <ul>
        <li><strong>We are the processor; you are the controller.</strong> DPA available, with SCCs + UK Addendum as a standing fallback.</li>
        <li><strong>Sub-processor register is generated per customer from live configuration</strong> — a customer using their own AI keys sees no AI sub-processor, rather than inheriting a static list that overstates their exposure. The baseline is human-reviewed so a new vendor cannot appear silently.</li>
        <li><strong>DSARs</strong> come through you, answered within <strong>30 days</strong>, with generated evidence.</li>
        <li><strong>Erasure works against our rollback snapshots.</strong> Each snapshot has its own key; erasure destroys it, content becomes unrecoverable, affected items are marked non-rollbackable. <strong>Erasure wins over recoverability.</strong></li>
        <li><strong>EU AI Act:</strong> advisory-only, human-approved use, assessed as <strong>not high-risk</strong>; transparency and record-keeping met by the receipt trail.</li>
      </ul>

      <h2>What we do not offer, and will not imply we do</h2>
      <ul>
        {NOT_OFFERED.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>

      <p>
        <strong>Contact:</strong> security@dedupr.cloud · acknowledgement within 72 hours ·{' '}
        <code>security.txt</code> published.
      </p>
    </PolicyPage>
  );
}
